import type { ReactNode } from 'react';
import { Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatCurrency';
import {
  deriveFlag,
  getDaysInMilestone,
  getDaysSinceLastActivity,
  getDaysStale
} from '../../activeLoanModel';
import type { ProspectLoan } from '../../types';
import { ActivityChip } from './ActivityChip';
import { ChannelTag } from './ChannelTag';
import { ConversationLogHoverCard } from './ConversationLogHoverCard';
import { InitialsAvatar } from './InitialsAvatar';
import { LoanNumberCell } from './LoanNumberCell';
import { FLAG_STRIPE_CLASS, WRAPPING_PILL_CLASS } from './loanRowClasses';
import { LockStatusPill } from './LockStatusPill';
import { MilestoneTrack } from './MilestoneTrack';
import { RiskFlag } from './RiskFlag';

/**
 * One loan as a stacked card: the narrow-width form of a Loan Center row. It
 * shows every field the full table shows, so nothing is lost when the table
 * no longer fits.
 */
interface LoanListCardProps {
  loan: ProspectLoan;
  onOpen: (loan: ProspectLoan) => void;
  /** Row selection; omit in views that have none (Hybrid). */
  selection?: { selected: boolean; onChange: (checked: boolean) => void };
  /** Row actions control; omit in views that have none (Hybrid). */
  actions?: ReactNode;
}

const LABEL_CLASS = 'text-[11px] font-medium uppercase tracking-wider text-muted-foreground';

// Raised above the card's open layer, and tall enough to tap.
const FLAG_TRIGGER_CLASS = 'relative z-10 min-h-6 max-w-full items-center';

function Field({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={cn('min-w-0', wide && 'col-span-2')}>
      <dt className={LABEL_CLASS}>{label}</dt>
      <dd className="mt-0.5 min-w-0 break-words text-sm text-foreground">{children}</dd>
    </div>
  );
}

export function LoanListCard({ loan, onOpen, selection, actions }: LoanListCardProps) {
  const isDeleted = loan.deleted_from_encompass === true;
  const flag = deriveFlag(loan);
  const daysStale = getDaysStale(loan);
  const daysSinceActivity = getDaysSinceLastActivity(loan);
  const hasConversationLogs = (loan.conversation_logs?.length ?? 0) > 0;
  const borrower = `${loan.borrower_first_name ?? ''} ${loan.borrower_last_name ?? ''}`.trim();
  const officerName = loan.loan_officer_first_name && loan.loan_officer_last_name
    ? `${loan.loan_officer_first_name} ${loan.loan_officer_last_name}`
    : null;
  // Synced verbatim from Encompass CX.LOAN.STATUS; blank sync values count as missing.
  const loanStatus = loan.cx_loan_status?.trim() || null;
  const cityState = [loan.subject_property_city, loan.subject_property_state].filter(Boolean).join(', ');

  return (
    <article
      className={cn(
        'relative flex h-full min-w-0 flex-col gap-3 rounded-md border bg-background p-3 hover:border-muted-foreground/30',
        selection?.selected && 'bg-muted/50',
        isDeleted && 'bg-red-50',
        !isDeleted && flag && FLAG_STRIPE_CLASS[flag]
      )}
    >
      <div className="flex items-start gap-2">
        {selection && (
          // The padded label is the tap target: a near miss still ticks the
          // box instead of opening the loan underneath.
          <label className="relative z-10 -m-2 flex p-2">
            <Checkbox
              className="mt-1"
              checked={selection.selected}
              onCheckedChange={checked => selection.onChange(checked === true)}
              aria-label={`Select ${loan.trans_details_loan_number}`}
            />
          </label>
        )}
        <InitialsAvatar name={borrower} size={24} />
        <div className="min-w-0 flex-1">
          {/* The name is the card's one primary control. Its ::after layer
              covers the card so the whole surface opens the loan, without
              nesting the other controls inside a button. */}
          <button
            type="button"
            onClick={() => onOpen(loan)}
            aria-label={borrower ? undefined : `Open loan ${loan.trans_details_loan_number ?? ''}`.trim()}
            className="block max-w-full break-words rounded-sm text-left text-sm font-medium text-foreground after:absolute after:inset-0 after:rounded-md after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
          >
            {borrower || '—'}
          </button>
          <div className="relative z-10 flex w-fit max-w-full min-w-0 flex-wrap items-center gap-1.5">
            {isDeleted && <Trash2 className="h-3.5 w-3.5 flex-shrink-0 text-red-500" aria-hidden />}
            <LoanNumberCell
              loanNumber={loan.trans_details_loan_number}
              className={cn('min-h-6', isDeleted && 'text-red-700')}
              wrap
            />
            {isDeleted && <Badge variant="destructive" className="text-[10px]">Deleted</Badge>}
          </div>
        </div>
        {actions && <div className="relative z-10 flex-shrink-0">{actions}</div>}
      </div>

      <MilestoneTrack loan={loan} daysInMilestone={getDaysInMilestone(loan)} />

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
        <Field label="Loan Status" wide>
          <span className={cn(!loanStatus && 'text-muted-foreground')}>{loanStatus ?? '—'}</span>
        </Field>
        <Field label="Amount">
          <span className="font-mono">{formatCurrency(loan.trans_details_loan_amt)}</span>
        </Field>
        <Field label="Purpose">{loan.trans_details_loan_purpose || '—'}</Field>
        <Field label="Property" wide>
          <div>{loan.property_address || '—'}</div>
          {cityState && <div className="text-[11px] text-muted-foreground">{cityState}</div>}
        </Field>
        <Field label="Officer">
          {officerName ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <InitialsAvatar name={officerName} size={20} />
              <span className="min-w-0 break-words">{loan.loan_officer_first_name}</span>
            </span>
          ) : (
            <span className="italic text-muted-foreground">Unassigned</span>
          )}
        </Field>
        <Field label="Channel">
          <ChannelTag channel={loan.channel} className={WRAPPING_PILL_CLASS} />
        </Field>
        <Field label="Lock">
          <LockStatusPill loan={loan} className={WRAPPING_PILL_CLASS} />
        </Field>
        {(flag === 'stalled' || hasConversationLogs) && (
          <Field label="Flag">
            {flag === 'stalled' ? (
              <ConversationLogHoverCard loan={loan} variant="stalled" days={daysStale} className={FLAG_TRIGGER_CLASS}>
                <RiskFlag flag="stalled" daysStale={daysStale} className={WRAPPING_PILL_CLASS} />
              </ConversationLogHoverCard>
            ) : (
              <ConversationLogHoverCard loan={loan} variant="active" days={daysSinceActivity} className={FLAG_TRIGGER_CLASS}>
                <ActivityChip days={daysSinceActivity} className={WRAPPING_PILL_CLASS} />
              </ConversationLogHoverCard>
            )}
          </Field>
        )}
      </dl>
    </article>
  );
}
